<template>
  <SearchableSelect
    :model-value="selectedValue"
    :options="options"
    :aria-label="t('station')"
    :track-by="['label']"
    class="min-w-56"
    @update:model-value="handleChange(String($event ?? ALL))"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SearchableSelect from '@/components/SearchableSelect.vue';
import type { Station } from '@/entities/station/station.entity';
import { useStations } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useLegalEntityFilter } from '@/hooks/use-legal-entity-filter';

/**
 * Searchable station filter, grouped by legal entity when there are several. The legal entities
 * are selectable headings above their stations; choosing one sets the global legal entity filter and clears the station. All stations stay on offer
 * while a legal entity filter is set; choosing a station of another legal entity switches the
 * filter to that legal entity so both never contradict each other.
 */
const stationId = defineModel<string | undefined>();

const ALL = '';
const LEGAL_ENTITY_PREFIX = 'legal-entity:';

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);
const { legalEntityId, legalEntityOptions, isLegalEntityFilterVisible } = useLegalEntityFilter();

const groups = computed(() =>
  legalEntityOptions.value.map((option) => ({
    legalEntityId: option.value,
    name: option.label,
    stations: (stations.value ?? []).filter((station) => station.legalEntity?.id === option.value),
  })),
);

const toStationOption = (station: Station) => ({
  label: getLocalizedField(station.info),
  value: station.id,
});

const options = computed(() =>
  isLegalEntityFilterVisible.value
    ? [
        { label: t('allLegalEntities'), value: ALL, isHeading: true },
        ...groups.value.flatMap((group) => [
          {
            label: group.name,
            value: `${LEGAL_ENTITY_PREFIX}${group.legalEntityId}`,
            isHeading: true,
          },
          ...group.stations.map(toStationOption),
        ]),
      ]
    : [{ label: t('allStations'), value: ALL }, ...(stations.value ?? []).map(toStationOption)],
);

const selectedValue = computed(() => {
  if (stationId.value) return stationId.value;
  if (legalEntityId.value) return `${LEGAL_ENTITY_PREFIX}${legalEntityId.value}`;
  return ALL;
});

const handleChange = (value: string) => {
  if (value.startsWith(LEGAL_ENTITY_PREFIX)) {
    legalEntityId.value = value.slice(LEGAL_ENTITY_PREFIX.length);
    stationId.value = undefined;
  } else if (value === ALL) {
    legalEntityId.value = null;
    stationId.value = undefined;
  } else {
    const stationLegalEntityId = stations.value?.find((station) => station.id === value)
      ?.legalEntity?.id;
    if (
      legalEntityId.value &&
      stationLegalEntityId &&
      stationLegalEntityId !== legalEntityId.value
    ) {
      legalEntityId.value = stationLegalEntityId;
    }
    stationId.value = value;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "station": "Station",
    "allStations": "All Stations",
    "allLegalEntities": "All legal entities"
  },
  "de": {
    "station": "Station",
    "allStations": "Alle Stationen",
    "allLegalEntities": "Alle Gesellschaften"
  },
  "es": {
    "station": "Estación",
    "allStations": "Todas las estaciones",
    "allLegalEntities": "Todas las sociedades"
  }
}
</i18n>
